import { PathItem } from "../types/PathItem";
import { getPathItem } from "./pathHelper";

/**
 * The export-control check asked before every image/document upload on the Images and
 * Documents screens (Add, and Capture for images).
 *
 * The popup collects the answer; the Function App is the boundary. It re-validates the answer,
 * derives the storage folder itself ("Export Controlled Technology - 2A291/images694-1/…") and
 * stamps who/when/what on the blob and in dbo.ExportControlLog (`ExportControlPolicy.cs`).
 * The form-field names and industry codes below are the wire contract with that file.
 */

export const EXPORT_CONTROL_INFO_URL =
  "https://www.help.cbp.gov/s/article/Article-1094?language=en_US";

export const OTHER_INDUSTRY_MAX_LENGTH = 254;

export type IndustryCode =
  | "military-defense"
  | "aviation-space"
  | "nuclear-energy"
  | "nuclear-weapons"
  | "chem-bio-weapons"
  | "semiconductor"
  | "other";

/** In the FSD's order — the server files a selection in this order too. */
export const INDUSTRIES: ReadonlyArray<{ code: IndustryCode; label: string }> = [
  { code: "military-defense", label: "Military / Defense" },
  { code: "aviation-space", label: "Aviation / Space Exploration" },
  { code: "nuclear-energy", label: "Nuclear Energy" },
  { code: "nuclear-weapons", label: "Nuclear Weapons" },
  { code: "chem-bio-weapons", label: "Chemical or Biological Weapons" },
  { code: "semiconductor", label: "Semiconductor Manufacturing" },
  { code: "other", label: "Other" },
];

/** What the popup hands to the upload once the user presses OK. */
export interface ExportControlAnswer {
  exportControlled: boolean;
  eccn?: string;
  noEccnListed?: boolean;
  industries?: IndustryCode[];
  otherIndustry?: string;
}

/** The popup's live form state. */
export interface ExportControlDraft {
  response: "" | "yes" | "no";
  eccn: string;
  noEccn: boolean;
  industries: IndustryCode[];
  otherIndustry: string;
}

export const EMPTY_DRAFT: ExportControlDraft = {
  response: "",
  eccn: "",
  noEccn: false,
  industries: [],
  otherIndustry: "",
};

/**
 * FSD: "typically a 5-digit alphanumeric number such as 2A291" — exactly five letters or
 * digits. Same rule as the server's `ExportControlPolicy.IsValidEccn`.
 */
export function isValidEccn(value: string): boolean {
  return /^[A-Z0-9]{5}$/.test(value.trim().toUpperCase());
}

export type DraftVerdict =
  | { ok: true; answer: ExportControlAnswer }
  | { ok: false; field: "response" | "eccn" | "industries" | "otherIndustry"; message: string };

/** Turns the form into an answer, or names the field that stops OK and why. */
export function validateDraft(draft: ExportControlDraft): DraftVerdict {
  if (draft.response === "no") {
    return { ok: true, answer: { exportControlled: false } };
  }
  if (draft.response !== "yes") {
    return { ok: false, field: "response", message: "Please select a response." };
  }

  if (!draft.noEccn) {
    const eccn = draft.eccn.trim().toUpperCase();
    if (!isValidEccn(eccn)) {
      return {
        ok: false,
        field: "eccn",
        message: "The ECCN must be exactly 5 letters or digits, for example 2A291.",
      };
    }
    return { ok: true, answer: { exportControlled: true, eccn } };
  }

  if (draft.industries.length === 0) {
    return {
      ok: false,
      field: "industries",
      message: "Please choose at least one industry/application that applies.",
    };
  }

  const industries = INDUSTRIES.map((i) => i.code).filter((c) => draft.industries.includes(c));
  let otherIndustry: string | undefined;
  if (industries.includes("other")) {
    otherIndustry = draft.otherIndustry.trim();
    if (!otherIndustry) {
      return { ok: false, field: "otherIndustry", message: 'Please describe the "Other" industry/application.' };
    }
    if (otherIndustry.length > OTHER_INDUSTRY_MAX_LENGTH) {
      return {
        ok: false,
        field: "otherIndustry",
        message: `The "Other" description can be at most ${OTHER_INDUSTRY_MAX_LENGTH} characters.`,
      };
    }
  }

  return { ok: true, answer: { exportControlled: true, noEccnListed: true, industries, otherIndustry } };
}

/** The multipart fields `uploadDocument` sends beside "file" (see ExportControlPolicy.Field*). */
export function toFormFields(answer: ExportControlAnswer): Record<string, string> {
  if (!answer.exportControlled) return { exportControlled: "N" };
  if (answer.noEccnListed) {
    const fields: Record<string, string> = {
      exportControlled: "Y",
      noEccn: "Y",
      industries: (answer.industries ?? []).join(","),
    };
    if (answer.otherIndustry) fields.otherIndustry = answer.otherIndustry;
    return fields;
  }
  return { exportControlled: "Y", eccn: answer.eccn ?? "" };
}

/** DocumentService lower-cases every blob path, so the listing returns it lower-case. */
const EC_FOLDER = /^(export controlled technology - [^/]+)\/(.*)$/i;

/**
 * Splits a listed path into the EC folder (if any) and the path beneath it — which is the
 * path the file would have had unclassified, e.g.
 * "export controlled technology - 2a291/images694-1/a.png" →
 * { ecFolder: "export controlled technology - 2a291", path: "images694-1/a.png" }.
 * That remainder is what the screens match against an order, so an EC file still shows on it.
 */
export function splitExportControlPath(path: string): { ecFolder?: string; path: string } {
  const match = EC_FOLDER.exec(path);
  return match ? { ecFolder: match[1], path: match[2] } : { path };
}

/** The listed paths with any EC folder removed — for matching a file to its order. */
export function withoutExportControlFolder(path: string): string {
  return splitExportControlPath(path).path;
}

/**
 * `getPathItem` for a listed path that may sit under an EC folder: the folder/fileName are
 * exactly what an unclassified file at the same place would get, and `ecFolder` is carried
 * so reads and deletes can name it (`?ecFolder=`).
 */
export function toDocumentPathItem(path: string): PathItem {
  const { ecFolder, path: rest } = splitExportControlPath(path);
  const item = getPathItem(rest);
  return ecFolder ? { ...item, ecFolder } : item;
}
